import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: number;
}

const Card = styled.div`
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #ebf5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 24px;
`;

const InfoContainer = styled.div<{ hasIcon: boolean }>`
  ${props => props.hasIcon && 'margin-left: 16px;'}
  flex: 1;
`;

const Title = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

const Value = styled.h3`
  font-size: 24px;
  font-weight: bold;
  color: #1f2937;
  margin: 4px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const TrendContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 8px;
`;

const TrendValue = styled.span<{ isPositive: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => (props.isPositive ? '#059669' : '#dc2626')};
`;

const TrendLabel = styled.span`
  font-size: 14px;
  color: #6b7280;
`;

export const StatCard = ({ title, value, subtitle, icon, trend }: StatCardProps) => {
  const { t } = useTranslation();

  return (
    <Card>
      <ContentWrapper>
        {icon && <IconWrapper>{icon}</IconWrapper>}
        <InfoContainer hasIcon={!!icon}>
          <Title>{title}</Title>
          <Value>{value}</Value>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {trend !== undefined && (
            <TrendContainer>
              <TrendValue isPositive={trend > 0}>
                {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
              </TrendValue>
              <TrendLabel>{t('stat-card:vs-last-month', 'vs last month')}</TrendLabel>
            </TrendContainer>
          )}
        </InfoContainer>
      </ContentWrapper>
    </Card>
  );
};